import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { MessagesSquare, ExternalLink, Circle, AlertCircle } from "lucide-react";

// Live Discord presence card — online count + member avatars + join button.
// Relies on the server's Discord Widget being enabled (Server Settings → Widget).
export function DiscordLive({ compact = false, adminHint = false }) {
  const { data, isLoading } = useQuery({
    queryKey: ["discord-widget"],
    queryFn: async () => (await api.get("/discord/widget")).data,
    refetchInterval: 30000,
  });

  if (isLoading) return null;
  if (!data?.enabled) {
    if (adminHint) {
      return (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="discord-widget-hint">
          <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-amber-300">Widget Discord non activé</div>
            <p className="text-xs text-muted-foreground mt-1">
              {data?.error || "Active-le sur Discord : Paramètres du serveur → Widget → « Activer le widget du serveur ». Les membres en ligne s'afficheront ici automatiquement."}
            </p>
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="rounded-lg border border-border overflow-hidden" data-testid="discord-live" style={{ background: "linear-gradient(135deg,#5865F214,transparent)" }}>
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
        <MessagesSquare className="w-5 h-5 shrink-0" style={{ color: "#5865F2" }} />
        <div className="min-w-0 flex-1">
          <div className="font-head font-semibold text-sm truncate">{data.name || "Discord"}</div>
          <div className="flex items-center gap-1.5 text-xs" style={{ color: "#22C55E" }}>
            <Circle className="w-2 h-2 fill-current" data-testid="discord-online-dot" />
            <span data-testid="discord-online-count">{data.presence_count}</span>
            <span className="text-muted-foreground">en ligne</span>
          </div>
        </div>
        {data.instant_invite && (
          <a href={data.instant_invite} target="_blank" rel="noopener noreferrer" data-testid="discord-live-join"
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-white transition-colors shrink-0"
            style={{ background: "#5865F2" }}>
            <ExternalLink className="w-3.5 h-3.5" /> Rejoindre
          </a>
        )}
      </div>
      {data.members?.length > 0 && (
        <div className="flex flex-wrap gap-2 p-4">
          {data.members.map((m, i) => (
            <div key={i} className="flex items-center gap-1.5 rounded-full bg-black/30 border border-border pl-1 pr-2.5 py-1" title={m.username}>
              <img src={m.avatar_url} alt="" className="w-5 h-5 rounded-full" />
              <span className="text-xs text-slate-300 max-w-[90px] truncate">{m.username}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
